import { useEffect, useState } from 'react';
import { ServerResponse, APIError } from '../services/qbeService';
import getPortalKey from '../utils/getPortalKey';
import { FormDataType } from 'contexts/FormProvider';
import { analytics, tools } from '@qbe/qal';
import { AxiosResponse } from 'axios';

interface EventPayload {
  event: string;
  eventAction: string;
  eventLabel?: string;
  data?: object;
}

// Utils
const NOT_SET = '(not set)';
const normaliseAddress = (address: string) => address.replace(/\s|,|:|-|_|\(|\)|'|\/|\./gi, '').toLowerCase();
const normaliseEmail = (email: string) => email.replace(/\s/gi, '').toLowerCase();
const normalisePhone = (phone: string) => phone.replace(/\s|-/gi, '').toLowerCase();
const getProfileDoamin = (str = '') => str.replace(/^.+@/i, '');
const getSuburbStatePostcode = (residentialAddress: string) => {
  const [suburb, state, postcode] = residentialAddress.split('  '); // double space
  return {
    suburb: suburb.replace(/^.*,\s([A-Z\s]+)$/, '$1'),
    state,
    postcode,
  };
};
const getHash = (str = '') => tools.hashHandler(str);
const start = {
  appStart: true,
  transactionStartView: true,
  transactionType: 'complaint',
};
const complete = {
  appComplete: true,
  transactionComplete: true,
  transactionType: 'complaint',
};

// Helpers
const getKPIHelper = (path: string) => {
  if (path === 'landing') return start;
  if (path === 'success') return complete;
  return null;
};

const resetEngagementTracking = (): void => {
  if (!window.qbeTrack?.engage) {
    return;
  }

  window.qbeTrack.engage.lastElement = null;
  window.qbeTrack.engage.engagedElements = null;
  window.qbeTrack.engage.files = [];
};

const getEventPayload = (eventPayload: EventPayload) => {
  const { event, eventAction, eventLabel, data } = eventPayload;

  return {
    event,
    eventAction,
    eventLabel,
    data,
  };
};

const augmentFormData = (formData: Partial<FormDataType>) => {
  const {
    mPhoneNumber,
    hPhoneNumber,
    oPhoneNumber,
    emailAddress,
    cEmailAddress,
    residentialAddress,
    rStreetAddress,
    rSuburb,
    rState,
    rPostcode,
  } = formData;

  const cPhoneNumber = mPhoneNumber || hPhoneNumber || oPhoneNumber;
  const suburbStatePostcode = residentialAddress ? getSuburbStatePostcode(residentialAddress) : null;
  const profileDomain = emailAddress ? getProfileDoamin(emailAddress) : null;
  const cPhoneNumberHash = cPhoneNumber ? getHash(normalisePhone(cPhoneNumber)) : null;
  const cEmailAddressHash = cEmailAddress ? getHash(normaliseEmail(cEmailAddress)) : null;
  const rawAddress = `${rStreetAddress}${rSuburb}${rState}${rPostcode}`;
  const addressHash = residentialAddress ? getHash(normaliseAddress(rawAddress)) : null;
  return {
    ...formData,
    ...suburbStatePostcode,
    profileDomain,
    cPhoneNumberHash,
    cEmailAddressHash,
    addressHash,
  };
};

// Exported helpers
export const setAppPartner = (): void => {
  const appPartner = getPortalKey();
  if (!appPartner || appPartner === 'qbe') return void 0;
  if (!window.qbeTrack?.meta) return void 0;
  window.qbeTrack.meta.appPartner = appPartner;
};

const getAppName = (): string | undefined => {
  return window.qbeTrack?.meta?.appName;
};

export const formatError = (errorType: string, errorMessage = NOT_SET, errorResponse = NOT_SET) => ({
  event: errorType,
  eventAction: errorMessage,
  eventLabel: errorResponse,
});

export const formatNext = (eventAction: string) => ({
  event: 'click_next_button',
  eventAction,
  eventLabel: NOT_SET,
});

// Exported Tracking Events
export const TrackEvent = (eventPayload: EventPayload, formData?: object): void => {
  const payload = getEventPayload(eventPayload);
  const metadata = formData ? augmentFormData(formData) : null;
  analytics.trackCustomEvent({
    ...payload,
    data: {
      ...metadata,
    },
  });
};

export const useTrackAlertBanner = (show: boolean, errorMessage: string): void => {
  useEffect(() => {
    if (show) {
      const payload = getEventPayload(formatError(errorMessage, 'banner'));
      TrackEvent(payload);
    }
  }, [show, errorMessage]);
};

export const useTrackError = (show: boolean, errorMessage?: string | null, errorLocation = 'display'): void => {
  useEffect(() => {
    if (show && errorMessage) {
      analytics.trackBusinessError({
        questionId: errorLocation,
        message: errorMessage,
      });
    }
  }, [show, errorMessage, errorLocation]);
};

export const useTrackApiError = (apiError?: ServerResponse): void => {
  useEffect(() => {
    const { ReturnCode = NOT_SET, ErrorMessage = NOT_SET, Response = NOT_SET } = apiError || {};
    const errorType = ReturnCode !== NOT_SET ? APIError[ReturnCode] || NOT_SET : NOT_SET;
    const errorCode = `{ReturnCode} - ({errorType})`;

    analytics.trackTechnicalError({
      errorCode,
      message: ErrorMessage,
      errorType,
      response: Response,
    });
  }, [apiError]);
};

export const trackAxiosResponse = (axiosResponse?: AxiosResponse): void => {
  const { data, status, statusText } = axiosResponse || {};
  const { errors } = data || [];
  const payload = {
    event: 'api_error',
    eventAction: statusText,
    eventLabel: errors[0] || NOT_SET,
    data: {
      errorCode: status || NOT_SET,
      errorType: errors?.toString() || NOT_SET,
    },
  };
  analytics.trackTechnicalError({
    errorCode: payload.event + ' ' + payload.data.errorCode + ' ' + payload.eventAction,
    message: payload.eventLabel,
    errorType: payload.data.errorType,
  });
};

export const useTrackSummary = (summaryPage: string): void => {
  useEffect(() => {
    const payload = {
      event: 'popup',
      eventAction: summaryPage,
    };
    TrackEvent(payload);
  }, [summaryPage]);
};

export const useTrackAbandon = (): void => {
  const onPageHide = () => {
    const { lastElement, engagedElements } = window.qbeTrack?.engage || {};
    return analytics.trackCustomEvent({
      event: 'abandon',
      eventAction: lastElement ?? NOT_SET,
      eventLabel: engagedElements?.join(),
    });
  };

  useEffect(() => {
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
    };
  });
};

export const useTrackPageView = (
  currentStep: number,
  stepsAnalytics: Array<string>,
  formData: Partial<FormDataType>
): void => {
  const [trackedStep, setTrackedStep] = useState<number>(99);
  useEffect(() => {
    setTrackedStep(currentStep);
    if (currentStep !== trackedStep) {
      const path = stepsAnalytics[currentStep];
      analytics.trackPageView({
        virtualPagePath: path,
        data: {
          ...getKPIHelper(path),
          ...augmentFormData(formData),
        },
      });
      resetEngagementTracking();
    }
  }, [currentStep, trackedStep, formData, stepsAnalytics]);
};

export const useTrackBasicPageView = (path: string): void => {
  useEffect(() => {
    analytics.trackPageView({
      virtualPagePath: path,
      data: {
        ...getKPIHelper(path),
      },
    });
  }, [path]);
};

export const setTransactionStartEvent = (): void => {
  analytics.trackTransactionStart({
    data: {
      transactionType: 'complaint',
    },
  });
};

export const setFormStartEvent = (): void => {
  analytics.trackFormStart();
};

export const setFormCompleteEvent = (): void => {
  analytics.trackFormComplete();
};

/*
  file: {
    lastModified: 1623817764191
    lastModifiedDate: Wed Jun 16 2021 14:29:24 GMT+1000 (Australian Eastern Standard Time) {}
    name: "IMG_20210612_125428.jpg"
    size: 816551
    type: "image/jpeg"
    webkitRelativePath: ""
  }
*/

interface File {
  name: string;
  size: number;
  type: string;
}

export const useTrackFiles = (files: File[]): void => {
  useEffect(() => {
    // Only track new files which are not in the store
    const newFiles = files.filter((file: File) => !window.qbeTrack?.engage.files.includes(file.name));
    newFiles.forEach((file: File) => {
      const { type, size } = file;
      const payload = {
        event: 'upload_file',
        eventAction: type || NOT_SET,
        eventLabel: NOT_SET, // Do not track file name as this could be PII
        eventValue: size,
      };
      return TrackEvent(payload);
    });
    // Add new files which have just been tracked to the store.
    // If a new pageview event occurs, this is reset. See resetEngagementTracking();
    if (window.qbeTrack) {
      window.qbeTrack.engage.files = [
        ...(window.qbeTrack.engage.files || []),
        ...(newFiles.map((file: File) => file.name) || []),
      ];
    }
  }, [files]);
};
